import React from "react";
import { FileText } from "lucide-react";

const LAST_UPDATED = "07 February 2026";

const Section = ({ title, children }) => (
  <section className="mt-10">
    <h2 className="text-2xl font-semibold text-brand-blue">{title}</h2>
    <div className="mt-3 space-y-3 text-brand-muted leading-relaxed">{children}</div>
  </section>
);

export default function Terms() {
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
      <FileText size={32} className="text-brand-orange mb-4" />
      <div className="overline mb-3">Legal</div>
      <h1 data-testid="terms-heading" className="text-4xl sm:text-5xl font-semibold text-brand-blue leading-tight">Terms of Service</h1>
      <p className="mt-3 text-sm text-brand-muted">Last updated: {LAST_UPDATED}</p>

      <p className="mt-8 text-lg text-brand-blue leading-relaxed">
        These Terms of Service ("Terms") govern your access to and use of the <strong>FuturePath Learning</strong>
        platform (the "Platform"), operated by <strong>FuturePath Learning</strong> (the "Company", "we", "us", or "our").
        By creating an account or using the Platform you agree to these Terms. If you do not agree, do not use the Platform.
      </p>

      <Section title="1. Eligibility">
        <p>
          The Platform is intended for parents, legal guardians, and educators who are at least 18 years old.
          By using the Platform you represent that you meet this requirement and have the legal capacity to enter into these Terms.
        </p>
      </Section>

      <Section title="2. Your account">
        <p>
          You are responsible for maintaining the confidentiality of your account credentials and for any activity
          that occurs under your account. You agree to notify us immediately of any unauthorised use. We may
          suspend or terminate accounts that violate these Terms, at our sole discretion.
        </p>
      </Section>

      <Section title="3. Nature of the service — not professional advice">
        <p>
          The Platform provides <strong>informational and guidance content only</strong>, including AI-generated
          reports, curriculum information, and career suggestions. It does <strong>not</strong> constitute
          professional educational, psychological, medical, legal, or financial advice. You are solely responsible
          for the education decisions you make for your child. Always consult qualified professionals for individual
          circumstances, especially regarding legal homeschooling requirements in your jurisdiction, special needs,
          or medical concerns.
        </p>
      </Section>

      <Section title="4. AI-generated content">
        <p>
          Reports, recommendations, and chat responses are generated by AI systems and may contain inaccuracies,
          omissions, or outdated information. We make no warranty that AI output is complete or correct. Use AI
          output as one input among many; verify important information independently.
        </p>
      </Section>

      <Section title="5. Third-party services (tutors, mentors, communities)">
        <p>
          Tutors, mentors, communities, and other third parties listed on the Platform are independent providers.
          We do not employ them, and we are not responsible for their services, safety, credentials, availability,
          conduct, or content. Any transaction, session, or membership you enter into with a third party is
          strictly between you and that third party. We recommend you conduct your own due diligence including
          background and credential checks before engaging.
        </p>
      </Section>

      <Section title="6. Payments and subscriptions (when enabled)">
        <p>
          Premium subscriptions, tutor bookings, and mentor sessions are billed via our payment processors.
          Prices are shown before purchase. Recurring subscriptions auto-renew unless cancelled at least 24
          hours before the next billing date. Refunds are handled per our published refund policy;
          fees paid to third-party tutors or mentors follow their individual refund terms.
        </p>
      </Section>

      <Section title="7. Acceptable use">
        <p>You agree not to:</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>Use the Platform for any unlawful, harmful, or fraudulent purpose.</li>
          <li>Impersonate any person or misrepresent your relationship to a child.</li>
          <li>Upload viruses, malware, or attempt to reverse engineer, scrape, or overload the Platform.</li>
          <li>Post content that is defamatory, obscene, discriminatory, or that infringes another person's rights.</li>
          <li>Use AI outputs to build a competing service or to train other AI models without written permission.</li>
          <li>Contact tutors, mentors, or community members off-platform to circumvent our fees or policies.</li>
        </ul>
      </Section>

      <Section title="8. Intellectual property">
        <p>
          All content, software, design, branding, and materials on the Platform are the property of the Company
          or its licensors and are protected by copyright, trademark, and other laws. You are granted a limited,
          revocable, non-exclusive, non-transferable licence to access and use the Platform for your personal,
          non-commercial use. You retain ownership of the content you submit; by submitting content you grant us
          a worldwide, royalty-free licence to store, display, and process it as needed to operate the Platform.
        </p>
      </Section>

      <Section title="9. User content">
        <p>
          You are responsible for the accuracy and legality of content you submit (assessment answers, messages,
          profiles). We may remove content that violates these Terms or applicable law without notice. We do not
          endorse and are not liable for user content.
        </p>
      </Section>

      <Section title="10. Privacy">
        <p>
          Your use of the Platform is also governed by our <a href="/privacy" className="text-brand-green underline">Privacy Policy</a>,
          which is incorporated into these Terms by reference.
        </p>
      </Section>

      <Section title="11. Disclaimers">
        <p>
          THE PLATFORM IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS,
          IMPLIED, OR STATUTORY, INCLUDING WITHOUT LIMITATION WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
          PARTICULAR PURPOSE, TITLE, ACCURACY, RELIABILITY, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE
          PLATFORM WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE, OR THAT ANY DEFECTS WILL BE CORRECTED.
        </p>
      </Section>

      <Section title="12. Limitation of liability">
        <p>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT SHALL THE COMPANY, ITS FOUNDER, OFFICERS,
          EMPLOYEES, AGENTS, OR AFFILIATES BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR
          PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, GOODWILL, OR EDUCATION OUTCOMES,
          ARISING OUT OF OR RELATED TO YOUR USE OF THE PLATFORM. OUR TOTAL AGGREGATE LIABILITY FOR ANY CLAIM
          ARISING OUT OF THESE TERMS OR THE PLATFORM SHALL NOT EXCEED THE GREATER OF (A) THE AMOUNT YOU PAID
          US IN THE 12 MONTHS PRECEDING THE CLAIM, OR (B) INR 1,000.
        </p>
      </Section>

      <Section title="13. Indemnification">
        <p>
          You agree to indemnify, defend, and hold harmless the Company and its
          affiliates from and against any claims, damages, losses, liabilities, costs, and expenses (including
          reasonable legal fees) arising out of your use of the Platform, your content, your breach of these
          Terms, or your violation of any law or third-party right.
        </p>
      </Section>

      <Section title="14. Termination">
        <p>
          We may suspend or terminate your access to the Platform at any time, with or without cause or notice,
          including for violation of these Terms. Upon termination, sections that by their nature should survive
          (IP, disclaimers, liability limitations, indemnification, governing law) will survive.
        </p>
      </Section>

      <Section title="15. Governing law and jurisdiction">
        <p>
          These Terms are governed by the laws of India, without regard to conflict-of-law principles. Any dispute
          arising out of or relating to the Platform or these Terms shall be subject to the exclusive jurisdiction
          of the competent courts located in India. Where permitted, disputes may be resolved by binding
          arbitration under the Arbitration and Conciliation Act, 1996.
        </p>
      </Section>

      <Section title="16. Changes to these Terms">
        <p>
          We may update these Terms from time to time. Material changes will be notified via email or in-app
          notice. Continued use of the Platform after the effective date constitutes acceptance.
        </p>
      </Section>

      <Section title="17. Contact">
        <p>
          For questions about these Terms, contact:<br />
          <strong>FuturePath Learning</strong><br />
          Email: <a href="mailto:futurepathleaening51@gmail.com" className="text-brand-green underline">futurepathleaening51@gmail.com</a><br />
          Website: <a href="https://future-path.in" target="_blank" rel="noreferrer" className="text-brand-green underline">future-path.in</a>
        </p>
      </Section>
    </div>
  );
}
